import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '../lib/supabase';
import { useVertical } from '../context/VerticalContext';
import { reportError } from '../lib/errors';
import {
    fetchActiveBroadcasts,
    broadcastToNotification,
    isBroadcastNotificationId,
    broadcastIdFromNotificationId,
    dismissBroadcast,
} from '../lib/broadcasts';

export interface Notification {
    id: string;
    org_id: string;
    user_id: string | null;
    title: string;
    message: string;
    type: 'info' | 'warning' | 'success' | 'error';
    is_read: boolean;
    metadata: Record<string, any>;
    created_at: string;
    read_at: string | null;
}

export const useNotifications = () => {
    const { activeOrgId } = useVertical();
    const [notifications, setNotifications] = useState<Notification[]>([]);
    // Derived, never stored. Two copies of the same fact drifted apart every
    // time a write half-succeeded: the badge cleared on click and refilled on
    // the next refresh, which read as "the notification came back".
    const unreadCount = notifications.filter((n) => !n.is_read).length;
    const [reviewCount, setReviewCount] = useState(0);
    const [expensesReviewCount, setExpensesReviewCount] = useState(0);
    const [pendingCreditsCount, setPendingCreditsCount] = useState(0);
    const [loading, setLoading] = useState(true);

    // Open supplier credits on the Credits & Write-offs screen. Kept in sync
    // with the dashboard "Needs Attention" card so the sidebar badge, the card,
    // and the notification bell all tell the same story.
    const PENDING_CREDIT_STATUSES = ['pending_collection', 'collected', 'credit_matched'];

    // Lets markAsRead / markAllAsRead re-read after a write that did not land,
    // without taking fetchNotifications as a dependency and rebuilding both
    // callbacks (and everything memoised on them) whenever the org changes.
    const refreshRef = useRef<(() => Promise<void>) | null>(null);

    // Fetch notifications
    const fetchNotifications = useCallback(async () => {
        if (!activeOrgId) return;

        const { data, error } = await supabase
            .from('notifications')
            .select('*')
            .order('created_at', { ascending: false })
            .limit(20);

        // Merge platform broadcasts (global, not org-scoped) into the feed.
        const broadcasts = (await fetchActiveBroadcasts()).map(broadcastToNotification);
        const rows = !error && data ? (data as Notification[]) : [];
        const merged = [...broadcasts, ...rows].sort(
            (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
        );
        setNotifications(merged);

        // Fetch pending OCR reviews
        const { count } = await supabase
            .from('ocr_review_queue')
            .select('*', { count: 'exact', head: true })
            .eq('org_id', activeOrgId)
            .eq('status', 'pending');

        if (count !== null) {
            setReviewCount(count);
        }

        // Fetch expenses awaiting review. These live in `processing_queue`
        // (OCR uploads the pipeline held back as needs_review/manual_entry) plus
        // any pending supplier-credit matches in `ocr_review_queue` — the same
        // two sources the dashboard "Needs Attention" card and the Review Queue
        // modal drain. `ledger_headers` never carries a needs_review status, so
        // the old query here was permanently zero and the badge never showed.
        const { count: queueCount } = await supabase
            .from('processing_queue')
            .select('*', { count: 'exact', head: true })
            .eq('org_id', activeOrgId)
            .in('status', ['needs_review', 'manual_entry']);

        setExpensesReviewCount((queueCount ?? 0) + (count ?? 0));

        // Fetch open supplier credits (Credits & Write-offs)
        const { count: creditsCount } = await supabase
            .from('part_returns')
            .select('*', { count: 'exact', head: true })
            .eq('org_id', activeOrgId)
            .in('status', PENDING_CREDIT_STATUSES);

        if (creditsCount !== null) {
            setPendingCreditsCount(creditsCount);
        }

        setLoading(false);
    }, [activeOrgId]);

    // Mark single notification as read
    const markAsRead = useCallback(async (notificationId: string) => {
        // Broadcasts aren't per-user rows — "read" is a local dismiss.
        if (isBroadcastNotificationId(notificationId)) {
            dismissBroadcast(broadcastIdFromNotificationId(notificationId));
            setNotifications(prev =>
                prev.map(n =>
                    n.id === notificationId ? { ...n, is_read: true } : n
                )
            );
            return;
        }

        const { data, error: fetchError } = await supabase.rpc('fn_mark_notification_read', {
            p_notification_id: notificationId
        });
        if (fetchError) reportError(fetchError, 'use-notifications.mark-notification-read');

        if (data) {
            setNotifications(prev =>
                prev.map(n =>
                    n.id === notificationId
                        ? { ...n, is_read: true, read_at: new Date().toISOString() }
                        : n
                )
            );
            return;
        }

        // `false` means the UPDATE matched no row — the notification is visible
        // but was not markable. That was routine until 20260820121000: the RPC
        // narrowed on profiles.active_org_id while the SELECT policy had been
        // widened to is_org_member, so anything from a non-active org came back
        // unread on every reload and no error was ever raised. It should now be
        // unreachable, so say so loudly and re-read rather than leaving the UI
        // asserting something the database did not do.
        if (!fetchError) {
            reportError(
                new Error(`fn_mark_notification_read matched no row for ${notificationId}`),
                'use-notifications.mark-notification-read',
            );
        }
        void refreshRef.current?.();
    }, []);

    // Delete a single notification (removes it from the feed entirely).
    // Broadcasts aren't per-user rows, so "delete" is a local dismiss.
    const deleteNotification = useCallback(async (notificationId: string) => {
        if (isBroadcastNotificationId(notificationId)) {
            dismissBroadcast(broadcastIdFromNotificationId(notificationId));
        } else {
            await supabase.from('notifications').delete().eq('id', notificationId);
        }
        setNotifications(prev => prev.filter(n => n.id !== notificationId));
    }, []);

    // Clear the whole feed — deletes this org's notifications and dismisses any
    // active broadcasts locally. RLS restricts the delete to rows the user owns.
    const clearAll = useCallback(async () => {
        setNotifications(prev => {
            prev.forEach(n => {
                if (isBroadcastNotificationId(n.id)) {
                    dismissBroadcast(broadcastIdFromNotificationId(n.id));
                }
            });
            return prev;
        });
        if (activeOrgId) {
            await supabase.from('notifications').delete().eq('org_id', activeOrgId);
        }
        setNotifications([]);
    }, [activeOrgId]);

    // Mark all as read
    const markAllAsRead = useCallback(async () => {
        // Dismiss any broadcasts currently in the feed locally.
        setNotifications(prev => {
            prev.forEach(n => {
                if (isBroadcastNotificationId(n.id)) {
                    dismissBroadcast(broadcastIdFromNotificationId(n.id));
                }
            });
            return prev.map(n => ({ ...n, is_read: true, read_at: new Date().toISOString() }));
        });
        const { error: markAllError } = await supabase.rpc('fn_mark_all_notifications_read');
        if (markAllError) {
            reportError(markAllError, 'use-notifications.mark-all-notifications-read');
            // The optimistic clear above is now a lie. Re-read so the bell shows
            // what is actually stored rather than a badge that empties on click
            // and refills on the next refresh.
            void refreshRef.current?.();
        }
    }, []);

    // Initial fetch
    useEffect(() => {
        refreshRef.current = fetchNotifications;
        void fetchNotifications();
    }, [fetchNotifications]);

    // Catch up whenever the app comes back to the foreground.
    //
    // The realtime subscription below is correct — `notifications` is in the
    // supabase_realtime publication and the org filter matches — but a mobile
    // browser SUSPENDS WebSockets while the tab is backgrounded, and an
    // installed PWA spends most of its life backgrounded. A quote accepted
    // while the phone was in a pocket produced a notification the socket never
    // delivered, and nothing re-read the table until a manual reload. That is
    // exactly the reported "it came, but late".
    //
    // Refetching on visibility and focus makes the socket an optimisation
    // rather than the only delivery path: worst case the badge is right the
    // moment the user looks at the screen.
    useEffect(() => {
        if (!activeOrgId) return;
        const catchUp = () => {
            if (document.visibilityState === 'visible') void fetchNotifications();
        };
        document.addEventListener('visibilitychange', catchUp);
        window.addEventListener('focus', catchUp);
        return () => {
            document.removeEventListener('visibilitychange', catchUp);
            window.removeEventListener('focus', catchUp);
        };
    }, [activeOrgId, fetchNotifications]);

    // Real-time subscription
    useEffect(() => {
        if (!activeOrgId) return;

        // Generate a unique ID for this hook instance's channels
        // to prevent collisions when multiple components use this hook simultaneously
        const instanceId = Math.random().toString(36).substring(7);

        const channel = supabase
            .channel(`notifications-${activeOrgId}-${instanceId}`)
            .on(
                'postgres_changes',
                {
                    event: 'INSERT',
                    schema: 'public',
                    table: 'notifications',
                    filter: `org_id=eq.${activeOrgId}`
                },
                (payload) => {
                    const newNotification = payload.new as Notification;
                    setNotifications(prev => [newNotification, ...prev].slice(0, 20));
                }
            )
            .on(
                'postgres_changes',
                {
                    event: 'UPDATE',
                    schema: 'public',
                    table: 'notifications',
                    filter: `org_id=eq.${activeOrgId}`
                },
                (payload) => {
                    const updated = payload.new as Notification;
                    setNotifications(prev => prev.map(n => (n.id === updated.id ? updated : n)));
                }
            )
            .subscribe();

        const reviewChannel = supabase
            .channel(`reviews-${activeOrgId}-${instanceId}`)
            .on(
                'postgres_changes',
                {
                    event: 'INSERT',
                    schema: 'public',
                    table: 'ocr_review_queue',
                    filter: `org_id=eq.${activeOrgId}`
                },
                (payload) => {
                    if (payload.new.status === 'pending') {
                        setReviewCount(prev => prev + 1);
                        setExpensesReviewCount(prev => prev + 1);
                    }
                }
            )
            .on(
                'postgres_changes',
                {
                    event: 'UPDATE',
                    schema: 'public',
                    table: 'ocr_review_queue',
                    filter: `org_id=eq.${activeOrgId}`
                },
                (payload) => {
                    if (payload.old.status === 'pending' && payload.new.status !== 'pending') {
                        setReviewCount(prev => Math.max(0, prev - 1));
                        setExpensesReviewCount(prev => Math.max(0, prev - 1));
                    } else if (payload.old.status !== 'pending' && payload.new.status === 'pending') {
                        setReviewCount(prev => prev + 1);
                        setExpensesReviewCount(prev => prev + 1);
                    }
                }
            )
            .subscribe();

        // Expenses awaiting review live in `processing_queue`
        // (needs_review/manual_entry). Keep the sidebar badge live as OCR
        // uploads land in — and are drained out of — the review queue.
        const REVIEW_QUEUE_STATUSES = ['needs_review', 'manual_entry'];
        const expensesReviewChannel = supabase
            .channel(`expenses-reviews-${activeOrgId}-${instanceId}`)
            .on(
                'postgres_changes',
                {
                    event: 'INSERT',
                    schema: 'public',
                    table: 'processing_queue',
                    filter: `org_id=eq.${activeOrgId}`
                },
                (payload) => {
                    if (REVIEW_QUEUE_STATUSES.includes(payload.new.status)) {
                        setExpensesReviewCount(prev => prev + 1);
                    }
                }
            )
            .on(
                'postgres_changes',
                {
                    event: 'UPDATE',
                    schema: 'public',
                    table: 'processing_queue',
                    filter: `org_id=eq.${activeOrgId}`
                },
                (payload) => {
                    const wasReview = REVIEW_QUEUE_STATUSES.includes(payload.old.status);
                    const isReview = REVIEW_QUEUE_STATUSES.includes(payload.new.status);
                    if (wasReview && !isReview) {
                        setExpensesReviewCount(prev => Math.max(0, prev - 1));
                    } else if (!wasReview && isReview) {
                        setExpensesReviewCount(prev => prev + 1);
                    }
                }
            )
            .subscribe();

        const creditsChannel = supabase
            .channel(`credits-${activeOrgId}-${instanceId}`)
            .on(
                'postgres_changes',
                {
                    event: 'INSERT',
                    schema: 'public',
                    table: 'part_returns',
                    filter: `org_id=eq.${activeOrgId}`
                },
                (payload) => {
                    if (PENDING_CREDIT_STATUSES.includes(payload.new.status)) {
                        setPendingCreditsCount(prev => prev + 1);
                    }
                }
            )
            .on(
                'postgres_changes',
                {
                    event: 'UPDATE',
                    schema: 'public',
                    table: 'part_returns',
                    filter: `org_id=eq.${activeOrgId}`
                },
                (payload) => {
                    const wasPending = PENDING_CREDIT_STATUSES.includes(payload.old.status);
                    const isPending = PENDING_CREDIT_STATUSES.includes(payload.new.status);
                    if (wasPending && !isPending) {
                        setPendingCreditsCount(prev => Math.max(0, prev - 1));
                    } else if (!wasPending && isPending) {
                        setPendingCreditsCount(prev => prev + 1);
                    }
                }
            )
            .subscribe();

        return () => {
            supabase.removeChannel(channel);
            supabase.removeChannel(reviewChannel);
            supabase.removeChannel(expensesReviewChannel);
            supabase.removeChannel(creditsChannel);
        };
    }, [activeOrgId]);

    return {
        notifications,
        unreadCount,
        reviewCount,
        expensesReviewCount,
        pendingCreditsCount,
        loading,
        markAsRead,
        markAllAsRead,
        deleteNotification,
        clearAll,
        refresh: fetchNotifications
    };
};
